/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';

/**
 * The human-readable window list shown before the user confirms (Requirement 6.6).
 *
 * Presentational only, and deliberately thinner than its siblings: the lines arrive already written
 * by `summariseWindows` in `schedule-display.js`, and nothing here recomputes them. That is what
 * keeps the overnight rule, the all-day rule and the group-not-defined wording stated once. This
 * component decides only how the lines are laid out, that the times carry the deployment timezone,
 * and that the overnight note is present.
 *
 * Two pieces of copy belong to this component. The timezone label, because a time shown without one
 * reads as the browser's local time, which it never is: the External_Scheduler interprets every
 * wall-clock time in `schedulerTimezone`, so a user elsewhere reading their own local time here
 * would calculate the wrong answer (Requirement 5.6). This is the only place in the modal that names
 * the timezone, so that it is stated once rather than repeated beside every control. And the overnight
 * note, because a stop time earlier than its start time closing on the following day is the least
 * obvious thing about the grammar, and the summary is where the user sees such a window written down
 * (Requirement 6.4).
 *
 * The title and the empty state are the third piece of copy this component owns. `Windows` was jargon
 * for the one thing the block is: the times this workspace runs. And `No windows yet.` reported an
 * absence without saying what the absence means, which mattered more once the current-state line under
 * the header was removed; the empty state now names the consequence, that starting and stopping is
 * left to the user. Still one line each: this modal was cut from 232 visible words to 56 and the
 * empty state is not the place to grow it back.
 *
 * Props:
 * - lines: [{ label, text }] from `summariseWindows`, one per window
 * - timezone: `schedulerTimezone` from the SchedulingInfo response, e.g. 'Australia/Sydney'
 */

// Short enough to sit beside the timezone badge, and worded as the modal already words it.
// `.wsch-modal-summary-title` renders the caption uppercase with letter-spacing, so at the modal's
// 520px width `WHEN THIS WORKSPACE RUNS` is wide enough to push the badge onto a second line.
// `Schedule Summary` also matches the Schedule tab it sits under, rather than adding a third phrasing
// for the same thing. What an absent schedule means is `EMPTY_TEXT`'s job, so the
// caption does not have to carry that too.
const TITLE_TEXT = 'Schedule Summary';

// States the consequence, not just the absence: nothing will start or stop this workspace on its own.
const EMPTY_TEXT = 'Nothing scheduled. This workspace will only start and stop when you do it yourself.';

class WorkspaceScheduleSummary extends React.Component {
  get lines() {
    // A blank line would render as an empty row that looks like a window with no hours, so blanks are
    // dropped rather than shown. Nothing else about a line is changed.
    return (this.props.lines || []).filter(
      line => line && typeof line.text === 'string' && line.text.trim().length > 0,
    );
  }

  renderTimezone() {
    const timezone = this.props.timezone;
    const named = typeof timezone === 'string' && timezone.trim().length > 0;

    // Without a timezone the badge still says the times are not local, because a missing setting is
    // no reason to let the user assume the one thing that is never true.
    return (
      <span className="wsch-modal-tz-badge" data-testid="schedule-summary-timezone">
        {named ? `Times in ${timezone.trim()}` : 'Times in the deployment timezone'}
      </span>
    );
  }

  renderEmpty() {
    return (
      <div className="wsch-modal-summary-empty" data-testid="schedule-summary-empty">
        {EMPTY_TEXT}
      </div>
    );
  }

  renderList(lines) {
    return (
      <ul className="wsch-modal-summary-list" data-testid="schedule-summary-list">
        {lines.map(line => (
          // A day's line has the day in its own column; a preset's line is one phrase across the row.
          <li
            className={`wsch-modal-summary-item${line.label ? '' : ' wsch-modal-summary-item-full'}`}
            key={`${line.label || ''}|${line.text}`}
          >
            {line.label ? <span className="wsch-modal-summary-day">{line.label}</span> : null}
            <span className="wsch-modal-summary-windows">{line.text}</span>
          </li>
        ))}
      </ul>
    );
  }

  render() {
    const lines = this.lines;

    return (
      <div className="wsch-modal-summary" data-testid="schedule-summary">
        <div className="wsch-modal-summary-title">
          <span>{TITLE_TEXT}</span>
          {this.renderTimezone()}
        </div>
        {lines.length === 0 ? this.renderEmpty() : this.renderList(lines)}
        {lines.length === 0 ? null : (
          <p className="wsch-modal-note" data-testid="schedule-summary-overnight-note">
            A stop time earlier than its start time closes the next day.
          </p>
        )}
      </div>
    );
  }
}

export default WorkspaceScheduleSummary;
export { TITLE_TEXT, EMPTY_TEXT };
